import { useEffect, useState } from 'react';
import { PROFILE_SORT_ORDER_OPTIONS, PROFILE_SORTABLE_COLUMNS } from '../../../config/enum-options.js';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi, normalizeList } from '../../../lib/admin-api/index.js';
import {
  buildDocumentsRequestDto,
  mapDocumentsResponseToDto,
} from '../../documents/dto/documents-list.dto.js';
import { buildDocumentsTableModel } from '../../documents/model/documents-list.model.js';
import {
  buildProfilesRequestDto,
  mapProfilesResponseToDto,
} from '../../profiles/dto/profile-list.dto.js';
import { buildProfilesTableModel } from '../../profiles/model/profile-list.model.js';
import {
  buildTransactionsRequestDto,
  mapTransactionsResponseToDto,
} from '../../transactions/dto/transactions-list.dto.js';
import { buildTransactionsTableModel } from '../../transactions/model/transactions-list.model.js';
import {
  DASHBOARD_RECENT_RECORDS_LIMIT,
  buildDashboardRecentSections,
} from '../model/recent-records.model.js';

function mapSettledResult(result, mapper) {
  if (result.status !== 'fulfilled') {
    return [];
  }

  return mapper(result.value);
}

function getFirstRejectedMessage(results) {
  const rejected = results.find((result) => result.status === 'rejected');
  return rejected?.reason?.message || '';
}

export function useDashboardRecentRecords() {
  const { token } = useAuth();
  const [sections, setSections] = useState(() => buildDashboardRecentSections());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isCancelled = false;

    const loadRecentRecords = async () => {
      setIsLoading(true);
      setError('');

      const profilePayload = buildProfilesRequestDto({}, {
        field: PROFILE_SORTABLE_COLUMNS.created,
        order: PROFILE_SORT_ORDER_OPTIONS.desc,
      }, {
        limit: DASHBOARD_RECENT_RECORDS_LIMIT,
        offset: 0,
      });
      const documentPayload = buildDocumentsRequestDto({}, {
        limit: DASHBOARD_RECENT_RECORDS_LIMIT,
        offset: 0,
      }, {
        sortBy: 'created',
        sortOrder: 'desc',
      });
      const transactionPagination = {
        limit: DASHBOARD_RECENT_RECORDS_LIMIT,
        offset: 0,
      };
      const transactionFilters = {
        sortBy: 'created',
        sortOrder: 'desc',
      };

      const results = await Promise.allSettled([
        adminApi.getProfiles(token, profilePayload),
        adminApi.getDocuments(token, documentPayload),
        adminApi.getTransactions(token, buildTransactionsRequestDto('deposit', transactionPagination, transactionFilters)),
        adminApi.getTransactions(token, buildTransactionsRequestDto('withdrawal', transactionPagination, transactionFilters)),
      ]);

      if (isCancelled) {
        return;
      }

      const profiles = mapSettledResult(results[0], (response) => (
        buildProfilesTableModel(mapProfilesResponseToDto(normalizeList(response)))
      ));
      const documents = mapSettledResult(results[1], (response) => (
        buildDocumentsTableModel(mapDocumentsResponseToDto(response))
      ));
      const deposits = mapSettledResult(results[2], (response) => (
        buildTransactionsTableModel(mapTransactionsResponseToDto(response))
      ));
      const withdrawals = mapSettledResult(results[3], (response) => (
        buildTransactionsTableModel(mapTransactionsResponseToDto(response))
      ));

      setSections(buildDashboardRecentSections({
        profiles,
        documents,
        deposits,
        withdrawals,
      }));
      setError(getFirstRejectedMessage(results));
      setIsLoading(false);
    };

    loadRecentRecords().catch((requestError) => {
      if (!isCancelled) {
        setSections(buildDashboardRecentSections());
        setError(requestError?.message || 'Failed to load recent records');
        setIsLoading(false);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [token]);

  return {
    recentSections: sections,
    isRecentLoading: isLoading,
    recentError: error,
  };
}
